/**
 * Guided Workspace Onboarding (Iteration 45).
 *
 * Auto-shown to admins on their first login into a fresh workspace. Admin can
 * also open it any time via Settings. The endpoint tracks its own completion
 * so this page just orchestrates the checklist and CTAs.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CheckCircle2, Circle, Sparkles, Users, Plug, Terminal, Tag, FileDown, ArrowRight, PlayCircle, X,
} from "lucide-react";

const VIDEO_ID = process.env.REACT_APP_ONBOARDING_VIDEO_ID || "";

const ICONS = {
  invite_team: Users,
  connect_integration: Plug,
  install_agent: Terminal,
  set_alias: Tag,
  first_handover: FileDown,
};

export default function Onboarding() {
  const nav = useNavigate();
  const { user, workspace } = useAuth();
  const [state, setState] = useState(null);
  const [alias, setAlias] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);
  const [busyStep, setBusyStep] = useState(null);

  const load = () => api.get("/workspace/onboarding-status").then((r) => setState(r.data)).catch(() => {});
  useEffect(load, []);

  const markManual = async (id) => {
    setBusyStep(id);
    try {
      await api.post(`/workspace/onboarding/step/${id}/complete`);
      toast.success("Marked done");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusyStep(null); }
  };

  const saveAlias = async () => {
    if (!alias.trim()) return;
    setSavingAlias(true);
    try {
      await api.post("/memory/aliases", { aliases: alias.split(/[\s,]+/).filter(Boolean) });
      toast.success("Alias saved");
      setAlias("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingAlias(false); }
  };

  const skip = async () => {
    await api.post("/workspace/onboarding/skip");
    toast.success("Setup guide dismissed — you can reopen it from Settings");
    nav("/app");
  };

  const [showVideo, setShowVideo] = useState(false);
  const videoDismissed = typeof window !== "undefined"
    ? localStorage.getItem("ragnes_onboarding_video_watched") === "1"
    : false;
  const [videoWatched, setVideoWatched] = useState(videoDismissed);
  const markVideoWatched = () => {
    try { localStorage.setItem("ragnes_onboarding_video_watched", "1"); } catch { /* ignore */ }
    setVideoWatched(true);
  };

  if (!state) return <div className="p-8 text-muted-foreground">Loading setup guide…</div>;

  const progress = state.completed_count / state.total_count;

  return (
    <div className="p-8 max-w-3xl mx-auto" data-testid="onboarding-page">
      <header className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Setup guide</p>
        <h1 className="font-display text-3xl mt-1">Set up Ragnes for {workspace?.name || "your workspace"}</h1>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress * 100}%` }} data-testid="onboarding-progress-bar" />
          </div>
          <span className="text-sm font-mono text-muted-foreground" data-testid="onboarding-progress-count">
            {state.completed_count} / {state.total_count}
          </span>
        </div>
      </header>

      {/* 90-sec walkthrough card — persists until watched or dismissed */}
      {!videoWatched && (
        <div
          className="mb-8 rounded-xl border border-sky-500/40 bg-gradient-to-br from-sky-500/5 to-fuchsia-500/5 p-5 flex items-center gap-5"
          data-testid="onboarding-video-card"
        >
          <button
            onClick={() => setShowVideo(true)}
            className="relative w-32 h-20 rounded-md bg-slate-900 border border-sky-500/40 overflow-hidden flex items-center justify-center hover:scale-[1.02] transition-transform shrink-0"
            aria-label="Play walkthrough"
            data-testid="onboarding-video-play"
          >
            <PlayCircle className="w-10 h-10 text-sky-400" />
            <span className="absolute bottom-1 right-1 text-[9px] font-mono bg-black/70 text-white px-1.5 rounded">
              1:30
            </span>
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">Watch first</p>
            <h2 className="font-display text-lg mt-1">The 90-second Ragnes walkthrough</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Meet the pieces before you set them up. Adds context to every step below.
            </p>
          </div>
          <button
            onClick={markVideoWatched}
            className="text-xs text-muted-foreground hover:text-foreground shrink-0"
            data-testid="onboarding-video-dismiss"
          >
            Skip →
          </button>
        </div>
      )}

      {showVideo && (
        <div className="fixed inset-0 z-[9998] bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="onboarding-video-modal" onClick={() => { setShowVideo(false); markVideoWatched(); }}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-3xl aspect-video rounded-xl border border-border/60 bg-black overflow-hidden relative shadow-2xl">
            <button
              onClick={() => { setShowVideo(false); markVideoWatched(); }}
              className="absolute top-3 right-3 z-10 rounded-full bg-black/70 hover:bg-black text-white w-8 h-8 flex items-center justify-center"
              aria-label="Close video"
              data-testid="onboarding-video-close">
              <X className="w-4 h-4" />
            </button>
            {VIDEO_ID ? (
              <iframe
                title="Ragnes walkthrough"
                src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0&modestbranding=1`}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                data-testid="onboarding-video-iframe"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-8 text-center text-slate-300">
                <PlayCircle className="w-12 h-12 text-sky-400 opacity-70" />
                <p className="font-display text-xl">Walkthrough coming soon</p>
                <p className="text-sm text-slate-400 max-w-sm">
                  Your CS team can drop a YouTube ID into <code className="text-xs bg-white/10 px-1.5 py-0.5 rounded">REACT_APP_ONBOARDING_VIDEO_ID</code> to enable the 90-sec Ragnes tour right here.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <ul className="space-y-3">
        {state.steps.map((step, idx) => {
          const Icon = ICONS[step.id] || Sparkles;
          return (
            <li key={step.id} data-testid={`onboarding-step-${step.id}`}
              className={`rounded-lg border p-5 transition-colors ${step.completed ? "border-emerald-500/40 bg-emerald-500/5" : "border-border/60"}`}>
              <div className="flex items-start gap-4">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${step.completed ? "bg-emerald-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                  {step.completed ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium">
                    <span className="text-xs font-mono text-muted-foreground mr-2">Step {idx + 1}</span>
                    {step.label}
                  </p>
                  <div className="mt-3 text-sm text-muted-foreground">
                    {step.id === "invite_team" && (
                      <>
                        <p>Add your employees so Ragnes can start capturing their knowledge.</p>
                        {!step.completed && <Link to="/app/members"><Button size="sm" className="mt-3 rounded-full" data-testid="onboarding-invite-cta">Invite team members →</Button></Link>}
                      </>
                    )}
                    {step.id === "connect_integration" && (
                      <>
                        <p>Connect Slack, Gmail, or GitHub to start capturing work activity automatically.</p>
                        {!step.completed && (
                          <Link to="/app/integrations"><Button size="sm" className="mt-3 rounded-full" data-testid="onboarding-integrations-cta">
                            Open integrations →
                          </Button></Link>
                        )}
                      </>
                    )}
                    {step.id === "install_agent" && (
                      <>
                        <p>The Ragnes desktop agent captures files, commits, and commands silently in the background.</p>
                        <pre className="mt-3 rounded-md bg-secondary/60 px-3 py-2 text-xs font-mono">npm install -g @ragnes/desktop-agent</pre>
                        {!step.completed && (
                          <Button size="sm" onClick={() => markManual("install_agent")} disabled={busyStep === "install_agent"}
                            className="mt-3 rounded-full" data-testid="onboarding-agent-done">
                            Mark as done
                          </Button>
                        )}
                      </>
                    )}
                    {step.id === "set_alias" && (
                      <>
                        <p>Aliases let your team query each other's bots. Set up /yourname to get started.</p>
                        {!step.completed && (
                          <div className="mt-3 flex gap-2">
                            <Input value={alias} onChange={(e) => setAlias(e.target.value)}
                              placeholder="marc mm marc-m" className="max-w-xs"
                              data-testid="onboarding-alias-input" />
                            <Button size="sm" onClick={saveAlias} disabled={savingAlias || !alias.trim()}
                              className="rounded-full" data-testid="onboarding-alias-save">
                              Save alias
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                    {step.id === "first_handover" && (
                      <>
                        <p>See what a handover pack looks like. Head to Individual memory, pick any teammate, and hit "Preview handover".</p>
                        {!step.completed && (
                          <Link to="/app/memory"><Button size="sm" className="mt-3 rounded-full" data-testid="onboarding-handover-cta">
                            Open Memory →
                          </Button></Link>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={skip} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">
          Skip setup for now →
        </button>
        {state.all_complete && (
          <Link to="/app">
            <Button className="rounded-full" data-testid="onboarding-done">
              I'm all set — go to dashboard <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
